import { useEffect, useState } from "react";
import { ArrowLeftIcon, TrashIcon } from "@heroicons/react/outline";
import { useDispatch, useSelector } from "react-redux";
import Cookies from "js-cookie";

import Overview from "./Overview";
import Features from "./Features";
import Currencies from "./Currencies";
import Subscription from "./Subscription";
import Button from "components/lib/Shared/Button";
import {
  deleteSubscriptionAsync,
  editSubscriptionAsync,
  getSubscriptionByIdAsync,
  getSubscriptionsAsync,
  updateSubscriptionState,
} from "state/slices/subscription";
import { useToast } from "hooks/useToast";
import { Constants } from "config/constants";
import ConfirmDialog from "components/lib/Shared/ConfirmDialog";
import DeleteButtonWithConfirmation from "components/lib/Shared/DeleteButtonWithConfirmation";
import NumberOfSubscribers from "./NumberOfSubscribers";

export default ({ setShowAddSubscriptionForm }) => {
  const cookieRole = Cookies.get("role");
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { selectedSubscription } = useSelector(
    (state) => state.subscriptionSlice
  );
  const { organization, organizationName } = useSelector(
    (state) => state.activeDashboardSlice
  );

  const resetSubscription = async () => {
    const res = await dispatch(
      getSubscriptionByIdAsync({
        subscriptionId: selectedSubscription._id,
      })
    );
    if (res?.payload?._id) {
      dispatch(
        updateSubscriptionState({
          selectedSubscription: res.payload,
          mode: "edit",
        })
      );
      await dispatch(getSubscriptionsAsync());
    }
  };

  useEffect(() => {
    return () => {
      dispatch(
        getSubscriptionsAsync({
          orgAdmin:
            organization && organization?.isOrgAdmin
              ? organization._id
              : undefined,
        })
      );
    };
  }, []);

  const handleExitWorkflow = () => {
    setShowAddSubscriptionForm(false);
    dispatch(
      updateSubscriptionState({
        selectedSubscription: {
          features: [],
          currencies: [],
        },
        mode: "",
      })
    );
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    if (selectedSubscription.status === "") {
      await dispatch(
        editSubscriptionAsync({
          subscriptionId: selectedSubscription?._id,
          data: { status: "Draft" },
        })
      );
      toast("success", "Saved subscription as draft");
    } else {
      await dispatch(
        editSubscriptionAsync({
          subscriptionId: selectedSubscription?._id,
          data: { status: "Active" },
        })
      );
      toast("success", "Published subscription");
    }
    handleExitWorkflow();
  };

  const handleDeleteSubscription = async () => {
    const response = await dispatch(
      deleteSubscriptionAsync({ subscriptionId: selectedSubscription._id })
    );
    if (response.error) {
      toast(
        "error",
        response.payload?.messageText ?? "Something went wrong please try again"
      );
    } else {
      dispatch(getSubscriptionsAsync());
    }
  };

  return (
    <div>
      <Button
        onClick={handleExitWorkflow}
        variant="text"
        className="flex space-x-2 text-sm font-medium rounded-md group text-gray-600 mb-2"
      >
        <ArrowLeftIcon
          className="w-5 h-5 text-gray-400 group-hover:text-gray-800"
          aria-hidden="true"
        />
        <span className="group-hover:text-gray-800">Exit</span>
      </Button>
      <div className="space-y-12">
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <div className="tile  col-span-1 md:col-span-2 lg:col-span-2">
            <div>
              <h2 className="text-base font-semibold leading-7 text-gray-900">
                Subscription Overview
              </h2>
            </div>
          </div>
          <div className="tile col-span-2 md:col-span-3 lg:col-span-6">
            <Overview />
          </div>
        </div>
        {selectedSubscription?._id && (
          <>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
              <div className="tile  col-span-1 md:col-span-2 lg:col-span-2">
                <div>
                  <h2 className="text-base font-semibold leading-7 text-gray-900">
                    Subscription Details
                  </h2>
                </div>
              </div>
              <div className="tile col-span-2 md:col-span-3 lg:col-span-6">
                <div className="my-2">
                  <Features resetSubscription={resetSubscription} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
              <div className="tile  col-span-1 md:col-span-2 lg:col-span-2">
                <div>
                  <h2 className="text-base font-semibold leading-7 text-gray-900">
                    Seats
                  </h2>
                </div>
              </div>
              <div className="tile col-span-2 md:col-span-3 lg:col-span-6">
                <div className="my-2">
                  <NumberOfSubscribers resetSubscription={resetSubscription} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
              <div className="tile  col-span-1 md:col-span-2 lg:col-span-2">
                <div>
                  <h2 className="text-base font-semibold leading-7 text-gray-900">
                    Currency Settings
                  </h2>
                </div>
              </div>
              <div className="tile col-span-2 md:col-span-3 lg:col-span-6">
                <div className="my-2">
                  <Currencies resetSubscription={resetSubscription} />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4">
              <div className="tile  col-span-1 md:col-span-2 lg:col-span-2">
                <div>
                  <h2 className="text-base font-semibold leading-7 text-gray-900">
                    Subscription Management
                  </h2>
                </div>
              </div>
              <div className="tile col-span-2 md:col-span-3 lg:col-span-6">
                <div className="my-2">
                  <Subscription resetSubscription={resetSubscription} />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-x-4 mt-1">
              <Button
                disabled={isSubmitting}
                variant="secondary"
                onClick={handleExitWorkflow}
              >
                "Cancel"
              </Button>
              {
                <>
                  {selectedSubscription.status !== "Active" && (
                    <div className="flex items-center gap-x-4 ">
                      <Button
                        type="submit"
                        loadingText="Saving..."
                        isLoading={isSubmitting}
                        onClick={handleSubmit}
                      >
                        {selectedSubscription.status === ""
                          ? "Save as draft"
                          : "Publish"}
                      </Button>
                    </div>
                  )}
                  <span>
                    <DeleteButtonWithConfirmation
                      confirmationModalName="delete_subscription_confirmation_modal"
                      overrideBaseStyles
                      btnClasses={`bg-red-600 text-white group flex rounded-md items-center w-full px-4 py-2 text-sm disabled:cursor-not-allowed disabled:text-gray-400`}
                      row={selectedSubscription}
                    />
                  </span>
                </>
              }
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        name="delete_subscription_confirmation_modal"
        onConfirm={handleDeleteSubscription}
      >
        Are you sure you want to permanently delete this entry?
      </ConfirmDialog>
    </div>
  );
};
